import { ComponentProps, useMemo, useState } from 'react'

import { HereMapApiService } from '@/shared/apis/heremap/HereMapApiService'
import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'

import { DropdownOption } from '../FilterMultiselectDropdown/FilterMultiselectDropdown'
import { SelectDropdown, SelectDropdownProps } from '../SelectDropdown/SelectDropdown'
import { TextField, TextFieldProps } from '../TextField/TextField'
import { TextFieldLabel } from '../TextField/TextFieldLabel'
import { AddressFieldMap } from './AddressFieldMap'
import { AddressFieldMapControl } from './AddressFieldMapControl'
import { HereMapItem } from './models/HereMapResponse'

export type AddressValue = {
  street?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
  mapRef?: string
  crossStreet?: string
  y?: number //latitude
  x?: number //longitude
}

export type AddressFieldsProps = Pick<ComponentProps<typeof StyledContainer>, 'sx'> & {
  value: AddressValue
  defaultValue?: AddressValue
  disabled?: boolean
  slotProps?: {
    street?: Omit<TextFieldProps, 'ref'>
    suburb?: Omit<TextFieldProps, 'ref'>
    state?: Omit<TextFieldProps, 'ref'>
    country?: Omit<TextFieldProps, 'ref'>
    postcode?: Omit<TextFieldProps, 'ref'>
    mapRef?: Omit<TextFieldProps, 'ref'>
    crossStreet?: Omit<TextFieldProps, 'ref'>
    searchAddress?: Omit<SelectDropdownProps, 'ref'>
    y?: Omit<TextFieldProps, 'ref'>
    x?: Omit<TextFieldProps, 'ref'>
  }
  error?: boolean
  onChange: (value: AddressValue) => void
  showMapRef?: boolean
  showCrossStreet?: boolean
}

export const AddressFields: React.FC<AddressFieldsProps> = ({ showMapRef, showCrossStreet, slotProps, error, disabled, ...props }) => {
  const [selectedAddress, setSelectedAddress] = useState<DropdownOption & HereMapItem | null>(null)
  const [addressOptions, setAddressOptions] = useState<HereMapItem[]>([])

  const addressDropdownOptions: DropdownOption[] = useMemo(() => {
    return addressOptions.map(item => ({
      value: item.id,
      label: item.title,
      ...item,
    }))
  }, [addressOptions])

  const onChanges = (key: keyof AddressValue, value: string | number) => {
    props.onChange({ ...props.value, [key]: value })
  }

  const throttledHereMapFetch = async (value: string) => {
    const results = await HereMapApiService.get('geocode', {
      q: value,
      in: 'countryCode:AUS,USA,NZL',
    })

    setAddressOptions(results.items)
  }

  const onAddressInputChanged = (_event: React.SyntheticEvent, value: string) => {
    if (value && value.length > 6) {
      throttledHereMapFetch(value)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onAddressOptionSelected = (_: React.SyntheticEvent, value: any) => {
    if (!value) return

    const hereMapItem: DropdownOption & HereMapItem = value as DropdownOption & HereMapItem

    setSelectedAddress(hereMapItem)
    props.onChange({
      ...props.value,
      street: `${hereMapItem.address?.houseNumber || ''} ${hereMapItem.address?.street || ''}`,
      suburb: hereMapItem.address?.district || value.address?.city || '',
      postcode: hereMapItem.address?.postalCode,
      state: hereMapItem.address?.state,
      country: hereMapItem.address?.countryName,
      y: hereMapItem.position?.lat,
      x: hereMapItem.position?.lng,
    })
  }

  // Map Right Click Event
  const onMapRightClick = (latitude: number, longitude: number) => {
    if (disabled) return

    props.onChange({
      ...props.value,
      y: latitude,
      x: longitude,
    })
  }

  return (
    <StyledContainer>
      <SelectDropdown
        label="Address Lookup"
        disabled={disabled}
        onInputChange={onAddressInputChanged}
        options={addressDropdownOptions}
        value={selectedAddress as DropdownOption}
        filterOptions={(options, _state) => options}
        helperText="Type a minimum of 7 characters to search for an address"
        onChange={onAddressOptionSelected}
        {...slotProps?.searchAddress}
      />
      <TextField
        label="Street"
        value={props.value?.street}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => onChanges('street', e.target.value)}
        error={error && !props.value?.street}
        helperText={error && !props.value?.street ? 'Street is required' : ''}
        required
        {...slotProps?.street}
      />
      <TextField
        label="Suburb"
        value={props.value?.suburb}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => onChanges('suburb', e.target.value)}
        error={error && !props.value?.suburb}
        helperText={error && !props.value?.suburb ? 'Suburb is required' : ''}
        required
        {...slotProps?.suburb}
      />
      <StyledRow>
        <TextField
          label="State"
          value={props.value?.state}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => onChanges('state', e.target.value)}
          error={error && !props.value?.state}
          helperText={error && !props.value?.state ? 'State is required' : ''}
          required
          sx={{
            flex: 9,
          }}
          {...slotProps?.state}
        />
        <TextField
          label="Postcode"
          disabled={disabled}
          value={props.value?.postcode}
          autoComplete="off"
          onChange={(e) => onChanges('postcode', e.target.value)}
          error={error && !props.value?.postcode}
          helperText={error && !props.value?.postcode ? 'Postcode is required' : ''}
          required
          sx={{
            flex: 3,
          }}
          {...slotProps?.postcode}
        />
      </StyledRow>
      <TextField
        label="Country"
        disabled={disabled}
        value={props.value?.country}
        autoComplete="off"
        onChange={(e) => onChanges('country', e.target.value)}
        error={error && !props.value?.country}
        helperText={error && !props.value?.country ? 'Country is required' : ''}
        required
        {...slotProps?.country}
      />
      {showMapRef && (
        <TextField
          label="Map Reference"
          disabled={disabled}
          value={props.value?.mapRef}
          onChange={(e) => onChanges('mapRef', e.target.value)}
          {...slotProps?.mapRef}
        />
      )}
      {showCrossStreet && (
        <TextField
          label="Cross Street"
          disabled={disabled}
          value={props.value?.crossStreet}
          onChange={(e) => onChanges('crossStreet', e.target.value)}
          {...slotProps?.crossStreet}
        />
      )}
      <StyledRow>
        <TextField
          label="Latitude (y)"
          type="number"
          disabled={disabled}
          value={props.value?.y || ''}
          onChange={(e) => onChanges('y', parseFloat(e.target.value))}
          helperText={
            props.value?.y !== undefined && (props.value?.y < -90 || props.value?.y > 90)
              ? 'Latitude must be between -90 and 90'
              : ''
          }
          inputProps={{ min: -90, max: 90, step: 'any' }}
          sx={{ flex: 6 }}
          {...slotProps?.y}
        />
        <TextField
          label="Longitude (x)"
          type="number"
          disabled={disabled}
          value={props.value?.x || ''}
          onChange={(e) => onChanges('x', parseFloat(e.target.value))}
          helperText={
            props.value?.x !== undefined && (props.value?.x < -180 || props.value?.x > 180)
              ? 'Longitude must be between -180 and 180'
              : ''
          }
          inputProps={{ min: -180, max: 180, step: 'any' }}
          sx={{ flex: 6 }}
          {...slotProps?.x}
        />
      </StyledRow>
      <TextFieldLabel sx={{ marginTop: pxToRem(8) }}>
        Map View (right click on the map to set latitude and longitude)
      </TextFieldLabel>
      <AddressFieldMap>
        <AddressFieldMapControl
          latitude={props.value?.y}
          longitude={props.value?.x}
          onMapRightClick={onMapRightClick}
        />
      </AddressFieldMap>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledRow = styled('div')`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    flex-direction: row;
    gap: ${(props) => props.theme.typography.pxToRem(12)};
  }
`
